'use no memo'

import React, { useState, useEffect } from 'react'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { Dialog, DialogContent, Typography, Box, Avatar, Divider, IconButton, Grid, FormLabel } from '@mui/material'

import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { Button } from '@/@core/components/mui/Button'
import { useNotification, useTranslator } from '@/hooks'
import { IUser, User, UserStatusEnum, useUpdateUserStatusMutation, getGuestmployeeSchema } from '@/@data'
import FormInput from '@/components/form/FormInput'
import { GuestWaitingNoteDialog } from './NoteGuestWaitingDialog'
import { getFullName } from '@/utils/getInitials'
import dayjs from 'dayjs'
import CompanySelection from '@/components/selection/CompanySelection'
import InputDateField from '@/components/common/input/InputDateField'
import BankSelection from '@/components/selection/BankSelection'
import { SelectField } from '../../common/input/SelectField'
import { EmployeeMappingDialog } from '@/components/user/employee/EmployeeMappingDialog'
import { yupResolver } from '@hookform/resolvers/yup'
import { DeleteUserDialog } from './DeleteUserDialog'

interface GuestAuthEmployeeProps {
  open: boolean
  onClose: () => void
  employee?: IUser
  refetch?: () => void
}

interface FieldState {
  checked: boolean
  value: string | number
}

interface FormState {
  personalInfo: Record<string, FieldState>
}

const GuestAuthEmployee: React.FC<GuestAuthEmployeeProps> = ({ open, onClose, employee, refetch }) => {
  const { __ } = useTranslator()
  const n = useNotification()
  const changeStatusMutation = useUpdateUserStatusMutation()

  // State for note dialog
  const [noteDialogOpen, setNoteDialogOpen] = useState(false)
  const [mappingDialogOpen, setMappingDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)

  const methods = useForm({
    defaultValues: {
      first_name: employee?.first_name || '',
      employee_id: employee?.employee_id || '',
      contract_type: employee?.contract_type || '',
      dob: employee?.birth_date || '',
      email: employee?.email || '',
      phone: employee?.phone || '',
      bank_id: employee?.bank_id || 0,
      bank_account_name: employee?.bank_account_name || '',
      // bank_name: employee?.bank?.name || '',
      bank_account_number: employee?.bank_account_number || '',
      working_date: employee?.working_date || '',
      companyName: employee?.company?.name || '',
      employeeId: employee?.company?.code || '',
      businessRegistration: employee?.company?.business_registration?.toString() || '',
      address: employee?.address || '',
      companyEmail: employee?.company?.email || '',
      companyPhone: employee?.company?.phone || '',
      department: employee?.company?.department || '',
      company_id: (employee?.company?.id || employee?.company_name || null) as number,
      joined_date: employee?.joined_date || '',
      identity_card: String(employee?.identity_card || ''),
      identity_card_issue_date: employee?.identity_card_issue_date || '',
      identity_card_issue_place: employee?.identity_card_issue_place || '',
      gender: employee?.gender || '',
      dependent: employee?.dependent || '',
      tenure: employee?.tenure || ''
    },
    resolver: yupResolver(getGuestmployeeSchema(__)),
    mode: 'onChange'
  })

  useEffect(() => {
    if (employee) {
      methods.reset({
        first_name: employee.first_name || '',
        employee_id: employee.employee_id || '',
        contract_type: employee.contract_type || '',
        dob: employee.birth_date || '',
        email: employee.email || '',
        phone: employee.phone || '',
        bank_id: employee.bank_id || 0,
        bank_account_name: employee?.bank_account_name || '',
        // bank_name: employee?.bank?.name || '',
        bank_account_number: employee.bank_account_number || '',
        working_date: employee.working_date || '',
        companyName: employee.company?.name || '',
        employeeId: employee.company?.code || '',
        businessRegistration: employee.company?.business_registration?.toString() || '',
        address: employee.address || '',
        companyEmail: employee.company?.email || '',
        companyPhone: employee.company?.phone || '',
        department: employee.company?.department || '',
        company_id: (employee.company?.id || employee?.company_name || null) as number,
        joined_date: employee.joined_date || '',
        identity_card: String(employee.identity_card || ''),
        identity_card_issue_date: employee.identity_card_issue_date || '',
        identity_card_issue_place: employee.identity_card_issue_place || '',
        gender: employee.gender || '',
        dependent: employee.dependent || '',
        tenure: employee.tenure || ''
      })
    }
  }, [employee, methods])

  // Auto-calculate tenure in months based on joined_date
  const joinedDateValue = methods.watch('joined_date') as string

  useEffect(() => {
    if (!joinedDateValue) {
      methods.setValue('tenure', '')

      return
    }

    const months = dayjs().diff(dayjs(joinedDateValue), 'month')

    methods.setValue('tenure', String(months))
  }, [joinedDateValue])

  const handleSubmit = async (data: any) => {
    // console.log('Form submitted:', data)
    if (!employee) return

    try {
      // Validate required fields before submission
      const requiredFields = [
        'company_id',
        'first_name',
        'bank_account_name',
        'bank_id',
        'employee_id',
        'bank_account_number'
      ]

      const missingFields = requiredFields.filter(field => {
        const value = data[field]

        return value === undefined || value === null || value === '' || (typeof value === 'number' && value === 0)
      })

      if (missingFields.length > 0) {
        n.error(__('Please fill in all required fields: ') + missingFields.join(', '))

        return
      }

      // Construct payload with status = active
      const payload = {
        ...data,
        status: UserStatusEnum.ACTIVE
      }

      await User.updateEmployee(employee.id, payload)
      n.success(__('User updated and activated successfully'))
      if (refetch) refetch()
      onClose()
    } catch (error: any) {
      if (error.status === 422 && error.message.length) {
        error.message.forEach((err: { name: string; errors: any[] }) => {
          methods.setError(err.name as keyof typeof methods.formState.errors, { message: `${err.errors.join(', ')}` })
        })

        return
      }

      n.error(error.message)
    }
  }

  const [formState, setFormState] = useState<FormState>({
    personalInfo: {
      first_name: { checked: false, value: employee?.first_name || '' },
      employee_id: { checked: false, value: employee?.employee_id || '' },
      joined_date: { checked: false, value: employee?.joined_date || '' },
      contract_type: { checked: false, value: employee?.contract_type || '' },
      phone: { checked: false, value: employee?.phone || '' },
      bank_id: { checked: false, value: employee?.bank_id as number },
      // bank_name: { checked: false, value: employee?.bank?.name || '' },
      bank_account_name: { checked: false, value: employee?.bank_account_name || '' },
      bank_account_number: { checked: false, value: employee?.bank_account_number || '' },
      identity_card: { checked: false, value: employee?.identity_card || '' },
      identity_card_issue_date: { checked: false, value: employee?.identity_card_issue_date || '' },
      identity_card_issue_place: { checked: false, value: employee?.identity_card_issue_place || '' },
      gender: { checked: false, value: employee?.gender || '' },
      address: { checked: false, value: employee?.address || '' },
      dob: {
        checked: false,
        value: employee?.birth_date
          ? dayjs(employee?.birth_date).format('DD/MM/YYYY')
          : dayjs(employee?.birth_date).format('DD/MM/YYYY')
      },
      email: { checked: false, value: employee?.email || '' },
      dependent: { checked: false, value: employee?.dependent || '' },
      tenure: { checked: false, value: employee?.tenure || '' },
      company_id: { checked: false, value: employee?.company?.id || employee?.company_name || 0 }
    }
  })

  // Add edit state
  const [editingField, setEditingField] = useState<{ section: 'personalInfo'; field: string } | null>(null)

  const handleFieldChange = (section: 'personalInfo', field: string, checked: boolean) => {
    setFormState(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: {
          ...prev[section][field],
          checked
        }
      }
    }))
  }

  const handleCheckAll = (section: 'personalInfo') => {
    const companyIdValue: unknown = methods.getValues('company_id') ?? formState.personalInfo.company_id.value

    let parsedCompanyId: number = NaN

    if (typeof companyIdValue === 'number') {
      parsedCompanyId = companyIdValue
    } else if (typeof companyIdValue === 'string') {
      const trimmedValue = companyIdValue.trim()

      parsedCompanyId = trimmedValue === '' ? NaN : Number(trimmedValue)
    }

    if (!Number.isFinite(parsedCompanyId)) {
      n.error(__('Please check the Employer field before checking all.'))

      return
    }

    const allChecked = Object.values(formState[section]).every(field => field.checked)
    const newState = { ...formState }

    Object.keys(newState[section]).forEach(field => {
      newState[section][field].checked = !allChecked
    })

    setFormState(newState)
  }

  const handleValueChange = (section: 'personalInfo', field: string, value: string) => {
    setFormState(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: {
          ...prev[section][field],
          value
        }
      }
    }))
  }

  const isAllChecked = (section: 'personalInfo') => {
    return Object.values(formState[section]).every(field => field.checked)
  }

  const renderField = (
    section: 'personalInfo',
    field: string,
    label: string,
    value: string | number,
    required: boolean = false,
    showControls: boolean = true
  ) => {
    const fieldState = formState[section][field]
    const isEditing = editingField && editingField.section === section && editingField.field === field
    const isRequired = (fieldState as any)?.required ?? required
    const isError = methods.formState.errors as Record<string, any>

    return (
      <Box key={field} sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
        <FormInput
          name={`${field}`}
          defaultValue={value}
          label={label}
          control={methods.control}
          onChange={e => handleValueChange(section, field, e.target.value)}
          transformValue={
            field === 'bank_account_name'
              ? value => (typeof value === 'string' ? value.toUpperCase() : value)
              : undefined
          }
          fullWidth
          size='small'
          sx={{
            flex: 1
          }}
          disabled={!isEditing && !isError[field]}
          required={isRequired}
          slotProps={
            showControls
              ? {
                  input: {
                    style: {
                      borderRadius: '50px'
                    },
                    endAdornment: (
                      <Box
                        className='flex items-center justify-center gap-1'
                        sx={{ display: 'flex', gap: 1, alignItems: 'center', justifyContent: 'center' }}
                      >
                        <IconButton
                          size='small'
                          sx={{
                            color: fieldState.checked ? '#28C76F' : '#2F2B3DE5',
                            padding: '0'
                          }}
                          onClick={() => handleFieldChange(section, field, !fieldState.checked)}
                        >
                          <i className='tabler-square-check' />
                        </IconButton>

                        {isEditing ? (
                          <IconButton
                            size='small'
                            sx={{
                              padding: '0',
                              color: '#FF4C51'
                            }}
                            onClick={() => setEditingField(null)}
                          >
                            <i className='tabler-square-x' />
                          </IconButton>
                        ) : (
                          <IconButton
                            size='small'
                            sx={{
                              padding: '0',
                              color: '#2F2B3DE5'
                            }}
                            onClick={() => setEditingField({ section, field })}
                          >
                            <i className='tabler-edit' />
                          </IconButton>
                        )}
                      </Box>
                    )
                  }
                }
              : {
                  input: {
                    style: {
                      borderRadius: '50px'
                    }
                  }
                }
          }
        />
      </Box>
    )
  }

  // Add this function inside the GuestAuthEmployee component
  const handleReset = () => {
    methods.reset({
      first_name: employee?.first_name || '',
      employee_id: employee?.employee_id || '',
      contract_type: employee?.contract_type || '',
      dob: employee?.birth_date || '',
      email: employee?.email || '',
      phone: employee?.phone || '',
      bank_id: employee?.bank_id || 0,
      bank_account_name: employee?.bank_account_name || '',
      // bank_name: employee?.bank?.name || '',
      bank_account_number: employee?.bank_account_number || '',
      working_date: employee?.working_date || '',
      companyName: employee?.company?.name || '',
      employeeId: employee?.company?.code || '',
      businessRegistration: employee?.company?.business_registration?.toString() || '',
      address: employee?.address || '',
      companyEmail: employee?.company?.email || '',
      companyPhone: employee?.company?.phone || '',
      department: employee?.company?.department || '',
      company_id: (employee?.company?.id || employee?.company_name || 0) as number,
      joined_date: employee?.joined_date || '',
      identity_card: String(employee?.identity_card || ''),
      identity_card_issue_date: employee?.identity_card_issue_date || '',
      identity_card_issue_place: employee?.identity_card_issue_place || '',
      gender: employee?.gender || '',
      dependent: employee?.dependent || '',
      tenure: employee?.tenure || ''
    })
    setFormState({
      personalInfo: {
        first_name: { checked: false, value: employee?.first_name || '' },
        employee_id: { checked: false, value: employee?.employee_id || '' },
        joined_date: { checked: false, value: employee?.joined_date || '' },
        contract_type: { checked: false, value: employee?.contract_type || '' },
        phone: { checked: false, value: employee?.phone || '' },
        bank_id: { checked: false, value: employee?.bank_id as number },
        // bank_name: { checked: false, value: employee?.bank?.name || '' },
        bank_account_name: { checked: false, value: employee?.bank_account_name || '' },
        bank_account_number: { checked: false, value: employee?.bank_account_number || '' },
        identity_card: { checked: false, value: employee?.identity_card || '' },
        identity_card_issue_date: { checked: false, value: employee?.identity_card_issue_date || '' },
        identity_card_issue_place: { checked: false, value: employee?.identity_card_issue_place || '' },
        gender: { checked: false, value: employee?.gender || '' },
        address: { checked: false, value: employee?.address || '' },
        dob: {
          checked: false,
          value: employee?.birth_date
            ? dayjs(employee?.birth_date).format('DD/MM/YYYY')
            : dayjs(employee?.birth_date).format('DD/MM/YYYY')
        },
        email: { checked: false, value: employee?.email || '' },
        dependent: { checked: false, value: employee?.dependent || '' },
        tenure: { checked: false, value: employee?.tenure || '' },
        company_id: { checked: false, value: employee?.company?.id || 0 }
      }
    })
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      scroll='body'
      fullWidth
      closeAfterTransition={false}
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '1270px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(handleSubmit)}>
          <DialogContent sx={{ p: 4 }}>
            <DialogCloseButton onClick={onClose} disableRipple>
              <i className='tabler-x' />
            </DialogCloseButton>
            {/* Header with user info */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
              <Avatar
                src={employee?.avatar_file?.path_url || employee?.avatar}
                alt={employee?.last_name}
                sx={{ width: 80, height: 80 }}
              />
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Typography variant='h5' gutterBottom sx={{ fontWeight: 'medium', fontSize: '28px', mb: 0 }}>
                    {getFullName(employee)}
                  </Typography>

                  <Button
                    variant='contained'
                    className='bg-[#6F83F6]'
                    endIcon={<i className='tabler-link' />}
                    sx={{ borderRadius: 10, height: '30px', fontSize: '13px' }}
                  >
                    {employee?.status === UserStatusEnum.GUEST_WAITING
                      ? __('Guest Waiting')
                      : employee?.status === UserStatusEnum.GUEST
                        ? __('Guest')
                        : employee?.status}
                  </Button>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }} className='text-[#2F2B3D66]'>
                  <i className='tabler-id' />
                  <span className='text-[15px] leading-[22px]'>{employee?.system_code}</span>
                  <i
                    className='tabler-copy'
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                      navigator.clipboard.writeText(employee?.system_code || '')
                      n.success(__('Copied to clipboard!'))
                    }}
                  />
                </Box>
              </Box>

              {/* Action buttons */}
              <Box sx={{ display: 'flex', gap: 6 }}>
                <Button
                  variant='contained'
                  startIcon={<i className='tabler-trash' />}
                  sx={{
                    borderRadius: 10,
                    backgroundColor: ' #FF4C5129',
                    color: '#FF4C51'
                  }}
                  onClick={() => setDeleteDialogOpen(true)}
                >
                  {__('Delete User')}
                </Button>
                <Button
                  variant='contained'
                  startIcon={
                    isAllChecked('personalInfo') ? <i className='tabler-x' /> : <i className='tabler-checks' />
                  }
                  onClick={() => {
                    handleCheckAll('personalInfo')
                  }}
                  sx={{
                    borderRadius: 10,
                    backgroundColor: isAllChecked('personalInfo') ? '#FF9800' : '#28C76F29',
                    color: isAllChecked('personalInfo') ? '#FFFFFF' : '#28C76F'
                  }}
                >
                  {isAllChecked('personalInfo') ? __('Un-Check All') : __('Check All')}
                </Button>
                <Button
                  variant='contained'
                  startIcon={<i className='tabler-refresh' />}
                  sx={{
                    borderRadius: 10,
                    backgroundColor: '#2E263D14',
                    color: '#2F2B3DE5'
                  }}
                  onClick={handleReset}
                >
                  {__('Reset')}
                </Button>
              </Box>
            </Box>

            {/* Personal Info Section */}
            <Box sx={{ mb: 4, border: '1px solid #E0E0E0', borderRadius: 2, p: 3 }}>
              <Typography variant='h6' sx={{ mb: 2 }}>
                {__('Personal Info')}
              </Typography>
              <Divider sx={{ mb: 3 }} />

              {/* <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3 }}> */}
              <Grid container spacing={4}>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'first_name',
                    __('Employee Name'),
                    formState.personalInfo.first_name.value as string,
                    true
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {(() => {
                    const isEditingCompany =
                      editingField && editingField.section === 'personalInfo' && editingField.field === 'company_id'

                    return (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative' }}
                        key='company_id'
                        className='custom-company-selection'
                      >
                        <Controller
                          name='company_id'
                          control={methods.control}
                          render={({ field }) => (
                            <CompanySelection
                              {...field}
                              fullWidth
                              label={__('Employer')}
                              onClear={() => methods.setValue('company_id', 0)}
                              disabled={!isEditingCompany}
                              showEndAdornment={false}
                              required
                              sx={{
                                '& .MuiSelect-select': {
                                  '&.MuiInputBase-input': {
                                    paddingInlineEnd: '80px !important'
                                  }
                                }
                              }}
                            />
                          )}
                        />
                        <Box
                          className='flex items-center justify-center gap-1'
                          sx={{
                            position: 'absolute',
                            right: '39px',
                            top: '67%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              color: formState.personalInfo.company_id.checked ? '#28C76F' : '#2F2B3DE5',
                              padding: '0'
                            }}
                            onClick={() =>
                              handleFieldChange(
                                'personalInfo',
                                'company_id',
                                !formState.personalInfo.company_id.checked
                              )
                            }
                          >
                            <i className='tabler-square-check' />
                          </IconButton>
                          {isEditingCompany ? (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#FF4C51' }}
                              onClick={() => setEditingField(null)}
                            >
                              <i className='tabler-square-x' />
                            </IconButton>
                          ) : (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#2F2B3DE5' }}
                              onClick={() => setEditingField({ section: 'personalInfo', field: 'company_id' })}
                            >
                              <i className='tabler-edit' />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'employee_id',
                    __('Employee Code'),
                    formState.personalInfo.employee_id.value as string,
                    true
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {(() => {
                    const isEditingJoinedDate =
                      editingField && editingField.section === 'personalInfo' && editingField.field === 'joined_date'

                    return (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative' }}
                        key='joined_date'
                      >
                        <Controller
                          name='joined_date'
                          control={methods.control}
                          render={({ field }) => (
                            <InputDateField
                              value={field.value}
                              placeholder={__('Select Date')}
                              onChange={field.onChange}
                              className='rounded-full py-2 w-full'
                              disabled={!isEditingJoinedDate}
                              checkdisabled={!isEditingJoinedDate}
                              label={__('Joined Date')}
                            />
                          )}
                        />
                        <Box
                          className='flex items-center justify-center gap-1'
                          sx={{
                            position: 'absolute',
                            right: '39px',
                            top: '67%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              color: formState.personalInfo.joined_date.checked ? '#28C76F' : '#2F2B3DE5',
                              padding: '0'
                            }}
                            onClick={() =>
                              handleFieldChange(
                                'personalInfo',
                                'joined_date',
                                !formState.personalInfo.joined_date.checked
                              )
                            }
                          >
                            <i className='tabler-square-check' />
                          </IconButton>
                          {isEditingJoinedDate ? (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#FF4C51' }}
                              onClick={() => setEditingField(null)}
                            >
                              <i className='tabler-square-x' />
                            </IconButton>
                          ) : (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#2F2B3DE5' }}
                              onClick={() => setEditingField({ section: 'personalInfo', field: 'joined_date' })}
                            >
                              <i className='tabler-edit' />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'bank_account_name',
                    __('Bank Account Name'),
                    formState.personalInfo.bank_account_name.value as string,
                    true
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {(() => {
                    const isEditingBankID =
                      editingField && editingField.section === 'personalInfo' && editingField.field === 'bank_id'

                    return (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative' }}
                        key='bank_id'
                      >
                        <Controller
                          name='bank_id'
                          control={methods.control}
                          render={({ field }) => (
                            <BankSelection
                              {...field}
                              fullWidth
                              label={__('Bank Name')}
                              onClear={() => methods.setValue('bank_id', 0)}
                              disabled={!isEditingBankID}
                              showEndAdornment={false}
                              wideEndAdornment={true}
                              sx={{
                                '& .MuiSelect-select': {
                                  '&.MuiInputBase-input': {
                                    paddingInlineEnd: '80px !important'
                                  }
                                }
                              }}
                              required
                            />
                          )}
                        />
                        <Box
                          className='flex items-center justify-center gap-1'
                          sx={{
                            position: 'absolute',
                            right: '39px',
                            top: '67%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              color: formState.personalInfo.bank_id.checked ? '#28C76F' : '#2F2B3DE5',
                              padding: '0'
                            }}
                            onClick={() =>
                              handleFieldChange('personalInfo', 'bank_id', !formState.personalInfo.bank_id.checked)
                            }
                          >
                            <i className='tabler-square-check' />
                          </IconButton>
                          {isEditingBankID ? (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#FF4C51' }}
                              onClick={() => setEditingField(null)}
                            >
                              <i className='tabler-square-x' />
                            </IconButton>
                          ) : (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#2F2B3DE5' }}
                              onClick={() => setEditingField({ section: 'personalInfo', field: 'bank_id' })}
                            >
                              <i className='tabler-edit' />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'bank_account_number',
                    __('Bank Account'),
                    formState.personalInfo.bank_account_number.value as string,
                    true
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'tenure',
                    __('Tenure'),
                    formState.personalInfo.tenure.value as string,
                    false,
                    false
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'identity_card',
                    __('Identity Card'),
                    formState.personalInfo.identity_card.value as string
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {(() => {
                    const isEditingIdentityCardDate =
                      editingField &&
                      editingField.section === 'personalInfo' &&
                      editingField.field === 'identity_card_issue_date'

                    return (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative' }}
                        key='identity_card_issue_date'
                      >
                        <Controller
                          name='identity_card_issue_date'
                          control={methods.control}
                          render={({ field }) => (
                            <InputDateField
                              value={field.value}
                              placeholder={__('Select Date')}
                              onChange={field.onChange}
                              className='rounded-full py-2 w-full'
                              disabled={!isEditingIdentityCardDate}
                              checkdisabled={!isEditingIdentityCardDate}
                              label={__('Identity Card Issue Date')}
                            />
                          )}
                        />
                        <Box
                          className='flex items-center justify-center gap-1'
                          sx={{
                            position: 'absolute',
                            right: '39px',
                            top: '67%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              color: formState.personalInfo.identity_card_issue_date.checked ? '#28C76F' : '#2F2B3DE5',
                              padding: '0'
                            }}
                            onClick={() =>
                              handleFieldChange(
                                'personalInfo',
                                'identity_card_issue_date',
                                !formState.personalInfo.identity_card_issue_date.checked
                              )
                            }
                          >
                            <i className='tabler-square-check' />
                          </IconButton>
                          {isEditingIdentityCardDate ? (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#FF4C51' }}
                              onClick={() => setEditingField(null)}
                            >
                              <i className='tabler-square-x' />
                            </IconButton>
                          ) : (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#2F2B3DE5' }}
                              onClick={() =>
                                setEditingField({ section: 'personalInfo', field: 'identity_card_issue_date' })
                              }
                            >
                              <i className='tabler-edit' />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'identity_card_issue_place',
                    __('Identity Card Issue Place'),
                    formState.personalInfo.identity_card_issue_place.value as string
                  )}
                </Grid>

                {/* <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField('personalInfo', 'gender', __('Gender'), formState.personalInfo.gender.value as string)}
                </Grid> */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {(() => {
                    const isEditingGender =
                      editingField && editingField.section === 'personalInfo' && editingField.field === 'gender'

                    return (
                      <Box sx={{ display: 'flex', flexDirection: 'column', position: 'relative' }} key='gender'>
                        <FormLabel className={isEditingGender ? 'text-[#2f2b3de6]' : 'text-[#2f2b3d66]'}>
                          {__('Gender')}
                        </FormLabel>
                        <Controller
                          name='gender'
                          control={methods.control}
                          render={({ field }) => (
                            <SelectField
                              {...field}
                              className='w-full py-2'
                              options={[
                                { label: __('Male'), value: 'male' },
                                { label: __('Female'), value: 'female' }
                              ]}
                              displayEmpty
                              disabled={!isEditingGender}
                              renderValue={value => {
                                if (!value) {
                                  return <span style={{ color: '#999' }}>{__('Select Gender')}</span>
                                }

                                return value === 'male' ? __('Male') : __('Female')
                              }}
                              fullWidth
                            />
                          )}
                        />
                        <Box
                          className='flex items-center justify-center gap-1'
                          sx={{
                            position: 'absolute',
                            right: '39px',
                            top: '67%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              color: formState.personalInfo.gender.checked ? '#28C76F' : '#2F2B3DE5',
                              padding: '0'
                            }}
                            onClick={() =>
                              handleFieldChange('personalInfo', 'gender', !formState.personalInfo.gender.checked)
                            }
                          >
                            <i className='tabler-square-check' />
                          </IconButton>
                          {isEditingGender ? (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#FF4C51' }}
                              onClick={() => setEditingField(null)}
                            >
                              <i className='tabler-square-x' />
                            </IconButton>
                          ) : (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#2F2B3DE5' }}
                              onClick={() => setEditingField({ section: 'personalInfo', field: 'gender' })}
                            >
                              <i className='tabler-edit' />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField('personalInfo', 'phone', __('Phone'), formState.personalInfo.phone.value as string)}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {(() => {
                    const isEditingDOB =
                      editingField && editingField.section === 'personalInfo' && editingField.field === 'dob'

                    return (
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, position: 'relative' }}
                        key='dob'
                      >
                        <Controller
                          name='dob'
                          control={methods.control}
                          render={({ field }) => (
                            <InputDateField
                              value={field.value}
                              placeholder={__('Select Date')}
                              onChange={field.onChange}
                              className='rounded-full py-2 w-full'
                              disabled={!isEditingDOB}
                              checkdisabled={!isEditingDOB}
                              label={__('D.O.B')}
                            />
                          )}
                        />
                        <Box
                          className='flex items-center justify-center gap-1'
                          sx={{
                            position: 'absolute',
                            right: '39px',
                            top: '67%',
                            transform: 'translateY(-50%)',
                            zIndex: 2
                          }}
                        >
                          <IconButton
                            size='small'
                            sx={{
                              color: formState.personalInfo.dob.checked ? '#28C76F' : '#2F2B3DE5',
                              padding: '0'
                            }}
                            onClick={() =>
                              handleFieldChange('personalInfo', 'dob', !formState.personalInfo.dob.checked)
                            }
                          >
                            <i className='tabler-square-check' />
                          </IconButton>
                          {isEditingDOB ? (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#FF4C51' }}
                              onClick={() => setEditingField(null)}
                            >
                              <i className='tabler-square-x' />
                            </IconButton>
                          ) : (
                            <IconButton
                              size='small'
                              sx={{ padding: '0', color: '#2F2B3DE5' }}
                              onClick={() => setEditingField({ section: 'personalInfo', field: 'dob' })}
                            >
                              <i className='tabler-edit' />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                    )
                  })()}
                </Grid>
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'dependent',
                    __('Dependent'),
                    formState.personalInfo.dependent.value as string
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  {renderField(
                    'personalInfo',
                    'contract_type',
                    __('Contract Type'),
                    formState.personalInfo.contract_type.value as string
                  )}
                </Grid>

                <Grid size={{ xs: 12, sm: 6, md: 12 }}>
                  {renderField(
                    'personalInfo',
                    'address',
                    __('Address'),
                    formState.personalInfo.address.value as string
                  )}
                </Grid>
              </Grid>
              {/* </Box> */}
            </Box>
          </DialogContent>

          {/* Footer Actions */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', my: 4, mx: 4 }}>
            <Button
              variant='contained'
              startIcon={<i className='tabler-arrow-left' />}
              onClick={onClose}
              sx={{ borderRadius: 10, backgroundColor: '#6F83F629', height: 38, color: '#6F83F6' }}
            >
              {__('Back To List')}
            </Button>

            <Box sx={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
              <Button
                variant='contained'
                startIcon={
                  changeStatusMutation.isPending ? (
                    <i className='tabler-loader-2 animate-spin' />
                  ) : (
                    <i className='tabler-lock' />
                  )
                }
                sx={{ borderRadius: 10, height: 38, backgroundColor: '#FF4C5129', color: '#FF4C51' }}
                disabled={changeStatusMutation.isPending}
                onClick={() => {
                  if (employee?.id) {
                    changeStatusMutation.mutate(
                      {
                        id: [employee.id],
                        status: UserStatusEnum.BLOCKED
                      },
                      {
                        onSuccess: () => {
                          n.success(__('User status updated to blocked successfully'))
                          refetch?.()
                          onClose()
                        },
                        onError: () => {
                          n.error(__('Failed to update user status'))
                        }
                      }
                    )
                  }
                }}
              >
                {changeStatusMutation.isPending ? __('Updating...') : __('Blocked')}
              </Button>
              <Button
                variant='contained'
                startIcon={<i className='tabler-calendar-time' />}
                sx={{ borderRadius: 10, height: 38, backgroundColor: '#6F83F629', color: '#6F83F6' }}
                disabled={changeStatusMutation.isPending}
                onClick={() => setNoteDialogOpen(true)}
              >
                {__('Guest Waiting')}
              </Button>

              <Button
                variant='contained'
                startIcon={<i className='tabler-link' />}
                sx={{ borderRadius: 10, height: 38, backgroundColor: '#FF9F43', color: '#ffffff' }}
                disabled={changeStatusMutation.isPending}
                onClick={() => setMappingDialogOpen(true)}
              >
                {__('Mapping Employee')}
              </Button>

              <Button
                type='submit'
                variant='contained'
                startIcon={<i className='tabler-check' />}
                sx={{
                  borderRadius: 10,
                  height: 38
                }}
                style={{
                  color: isAllChecked('personalInfo') ? '#FFFFFF' : '#999999',
                  backgroundColor: isAllChecked('personalInfo') ? '#28C76F' : '#EAEAEC'
                }}
                disabled={!isAllChecked('personalInfo')}
              >
                {__('Confirm Information')}
              </Button>
            </Box>
          </Box>
        </form>
      </FormProvider>

      <DeleteUserDialog
        open={deleteDialogOpen}
        setOpen={setDeleteDialogOpen}
        dataDelete={employee}
        onSuccess={() => {
          refetch?.()
          onClose()
        }}
      />

      {/* Note Dialog for Guest Waiting */}
      <GuestWaitingNoteDialog
        open={noteDialogOpen}
        setOpen={setNoteDialogOpen}
        dataRequest={employee}
        onSuccess={() => {
          refetch?.()
          onClose()
        }}
      />

      <EmployeeMappingDialog
        open={mappingDialogOpen}
        onClose={() => setMappingDialogOpen(false)}
        employee={employee}
        onSuccess={() => {
          refetch?.()
          onClose()
        }}
      />
    </Dialog>
  )
}

export default GuestAuthEmployee
